import {
  adminTeamSchema,
  LINEUP_MODE_LABELS,
  LINEUP_MODES,
  lineupModeSchema,
  mediaVariant,
  TEAM_KIND_HINTS,
  TEAM_KIND_LABELS,
  TEAM_KINDS,
  teamAccountSchema,
  teamBrand,
  teamCatalogSchema,
  teamKindSchema,
  teamListSchema,
  type AdminTeam,
  type LineupMode,
  type TeamCatalog,
  type TeamDivision,
  type TeamGame,
  type TeamKind,
  type TeamList,
} from '@growup/site-shared';
import {
  Autocomplete,
  Button,
  Card,
  Checkbox,
  cx,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  Textarea,
} from '@growup/ui';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { ImageField, useImagesBusy } from '../media/ImageUpload';
import { CalendarCard } from './CalendarCard';
import { LineupCard } from './LineupCard';
import { CommonRulesView, TeamRulesCard } from './RulesCards';
import { SheetsCard } from './SheetsCard';
import { ShirtsCard } from './ShirtsCard';
import { TripsCard } from './TripsCard';

const BASE = '/api/site/teams';

type Tab = 'equipas' | 'jogos' | 'regulamento';

/**
 * Equipas de competicao, parte 1 (decidido pelo GrowuP a 24-10-2026; ver
 * docs/equipas): equipas, jogos e modalidades. O Gestor de Equipas ve tudo;
 * o Manager de equipa so as suas, e nelas so muda a apresentacao.
 */
export function TeamsSection() {
  const [tab, setTab] = useState<Tab>('equipas');
  const [canManageAll, setCanManageAll] = useState(false);
  const [catalog, setCatalog] = useState<TeamCatalog | null>(null);
  const [teams, setTeams] = useState<AdminTeam[] | null>(null);
  const [partners, setPartners] = useState<TeamList['partners']>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson(BASE, teamListSchema)
      .then((list) => {
        setCanManageAll(list.canManageAll);
        setCatalog(list.catalog);
        setTeams(list.teams);
        setPartners(list.partners);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  const tabs: { id: Tab; label: string }[] = canManageAll
    ? [
        { id: 'equipas', label: 'Equipas' },
        { id: 'jogos', label: 'Jogos e modalidades' },
        { id: 'regulamento', label: 'Regulamento comum' },
      ]
    : [{ id: 'equipas', label: 'As minhas equipas' }];

  return (
    <div className="flex flex-col gap-4">
      <nav
        className="flex max-w-full gap-1 overflow-x-auto [scrollbar-width:none]"
        aria-label="Equipas"
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            aria-current={tab === t.id ? 'page' : undefined}
            onClick={() => setTab(t.id)}
            className={cx(
              'shrink-0 whitespace-nowrap border-b-2 px-3 py-2 font-sub text-label font-bold uppercase',
              tab === t.id
                ? 'border-gu-accent text-gu-ink'
                : 'border-transparent text-gu-ink-muted hover:text-gu-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {catalog === null || teams === null ? (
        !error ? (
          <p>A carregar…</p>
        ) : null
      ) : tab === 'jogos' && canManageAll ? (
        <GamesView catalog={catalog} onChange={setCatalog} />
      ) : tab === 'regulamento' && canManageAll ? (
        <CommonRulesView />
      ) : (
        <TeamsView
          canManageAll={canManageAll}
          catalog={catalog}
          teams={teams}
          partners={partners}
          onSaved={(saved) =>
            setTeams((list) =>
              list?.some((t) => t.id === saved.id)
                ? list.map((t) => (t.id === saved.id ? saved : t))
                : [...(list ?? []), saved],
            )
          }
          onReload={load}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lista de equipas
// ---------------------------------------------------------------------------

type StatusFilter = 'active' | 'archived';

function TeamsView({
  canManageAll,
  catalog,
  teams,
  partners,
  onSaved,
  onReload,
}: {
  canManageAll: boolean;
  catalog: TeamCatalog;
  teams: AdminTeam[];
  partners: TeamList['partners'];
  onSaved: (t: AdminTeam) => void;
  onReload: () => void;
}) {
  const [divisionId, setDivisionId] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  // Muda so ao abrir a ficha: criar uma equipa nao a volta a montar.
  const [opened, setOpened] = useState(0);
  const open = (id: string | 'new') => {
    setOpened((n) => n + 1);
    setEditing(id);
  };

  if (editing !== null) {
    const team = teams.find((t) => t.id === editing) ?? null;
    return (
      <TeamEditor
        key={opened}
        team={team}
        catalog={catalog}
        partners={partners}
        canManageAll={canManageAll}
        onSaved={(saved) => {
          onSaved(saved);
          setEditing(saved.id);
        }}
        onClose={() => {
          setEditing(null);
          onReload();
        }}
      />
    );
  }

  const games = new Map(catalog.games.map((g) => [g.id, g]));
  const shown = teams.filter(
    (t) =>
      t.status === status && (divisionId === '' || games.get(t.gameId)?.divisionId === divisionId),
  );
  const archivedCount = teams.filter((t) => t.status === 'archived').length;

  return (
    <Card
      headingLevel={2}
      title={canManageAll ? 'Equipas' : 'As minhas equipas'}
      actions={
        canManageAll ? (
          <Button size="sm" onClick={() => open('new')}>
            Nova equipa
          </Button>
        ) : null
      }
    >
      <div className="flex flex-col gap-4">
        {canManageAll ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:max-w-xl">
            <Field label="Modalidade">
              <Select value={divisionId} onChange={(e) => setDivisionId(e.target.value)}>
                <option value="">Todas</option>
                {catalog.divisions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Estado">
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value === 'archived' ? 'archived' : 'active')}
              >
                <option value="active">Ativas</option>
                <option value="archived">Arquivadas ({archivedCount})</option>
              </Select>
            </Field>
          </div>
        ) : (
          <p className="text-gu-ink-muted">
            As equipas que o Gestor de Equipas lhe atribuiu. Pode mudar a descrição, o logótipo e a
            foto.
          </p>
        )}
        {shown.length === 0 ? (
          <p>
            {canManageAll
              ? status === 'active'
                ? 'Ainda não há equipas ativas aqui.'
                : 'Não há equipas arquivadas aqui.'
              : 'Ainda não lhe foi atribuída nenhuma equipa.'}
          </p>
        ) : (
          <TeamGroups teams={shown} catalog={catalog} onOpen={open} />
        )}
      </div>
    </Card>
  );
}

/** Por modalidade e depois por jogo, como vai ser a pagina publica. */
function TeamGroups({
  teams,
  catalog,
  onOpen,
}: {
  teams: AdminTeam[];
  catalog: TeamCatalog;
  onOpen: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      {catalog.divisions.map((division) => {
        const games = catalog.games.filter((g) => g.divisionId === division.id);
        const inDivision = teams.filter((t) => games.some((g) => g.id === t.gameId));
        if (inDivision.length === 0) return null;
        return (
          <section key={division.id} className="flex flex-col gap-2">
            <h3 className="font-display text-title text-gu-ink">
              {division.name}
              {division.brandName ? (
                <span className="ml-2 font-sub text-label text-gu-ink-muted">
                  {division.brandName}
                </span>
              ) : null}
            </h3>
            <ul className="flex flex-col gap-2">
              {inDivision.map((t) => (
                <li key={t.id}>
                  <TeamRow
                    team={t}
                    game={games.find((g) => g.id === t.gameId)}
                    division={division}
                    onOpen={() => onOpen(t.id)}
                  />
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function TeamRow({
  team,
  game,
  division,
  onOpen,
}: {
  team: AdminTeam;
  game: TeamGame | undefined;
  division: TeamDivision;
  onOpen: () => void;
}) {
  const brand = teamBrand(team, division);
  return (
    <div className="flex flex-wrap items-center gap-4 border-2 border-gu-line p-2">
      {team.logoUrl ? (
        <img
          src={mediaVariant(team.logoUrl, 'sm')}
          alt=""
          className="size-12 bg-gu-sunken object-contain p-1"
        />
      ) : (
        <div className="flex size-12 items-center justify-center border border-dashed border-gu-line text-caption text-gu-ink-muted">
          —
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="font-semibold">{team.name}</span>
        <span className="text-caption text-gu-ink-muted">
          {[
            game?.name ?? '—',
            TEAM_KIND_LABELS[team.kind],
            LINEUP_MODE_LABELS[team.lineupMode],
            brand,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        <span className="flex flex-wrap gap-2">
          {team.academy ? <StatusBadge tone="info">Academia</StatusBadge> : null}
          {team.status === 'archived' ? <StatusBadge tone="neutral">Arquivada</StatusBadge> : null}
          {team.managers.length > 0 ? (
            <span className="text-caption text-gu-ink-muted">
              Manager: {team.managers.map((m) => m.login).join(', ')}
            </span>
          ) : null}
        </span>
      </div>
      <Button size="sm" variant="secondary" onClick={onOpen}>
        Abrir
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ficha da equipa
// ---------------------------------------------------------------------------

function TeamEditor({
  team,
  catalog,
  partners,
  canManageAll,
  onSaved,
  onClose,
}: {
  team: AdminTeam | null;
  catalog: TeamCatalog;
  partners: TeamList['partners'];
  canManageAll: boolean;
  onSaved: (t: AdminTeam) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(team?.name ?? '');
  const [gameId, setGameId] = useState(team?.gameId ?? '');
  const [kind, setKind] = useState<TeamKind>(team?.kind ?? 'group');
  const [lineupMode, setLineupMode] = useState<LineupMode>(team?.lineupMode ?? 'season');
  const [academy, setAcademy] = useState(team?.academy ?? false);
  const [brandName, setBrandName] = useState(team?.brandName ?? '');
  const [logoUrl, setLogoUrl] = useState<string | null>(team?.logoUrl ?? null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(team?.photoUrl ?? null);
  const [description, setDescription] = useState(team?.description ?? '');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const images = useImagesBusy();

  const game = catalog.games.find((g) => g.id === gameId);
  const division = catalog.divisions.find((d) => d.id === game?.divisionId);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(null);
    setFields({});
    try {
      const res = !canManageAll
        ? await send('PUT', `${BASE}/${team?.id ?? ''}/presentation`, {
            logoUrl,
            photoUrl,
            description,
          })
        : await send(team ? 'PUT' : 'POST', team ? `${BASE}/${team.id}` : BASE, {
            name,
            gameId,
            kind,
            lineupMode,
            academy,
            brandName,
            logoUrl,
            photoUrl,
            description,
          });
      onSaved(adminTeamSchema.parse(res));
      setSaved(team ? 'Guardado.' : 'Equipa criada.');
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (path: 'archive' | 'restore') => {
    if (!team) return;
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      onSaved(adminTeamSchema.parse(await send('POST', `${BASE}/${team.id}/${path}`)));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const locked = !canManageAll;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          ← Equipas
        </Button>
      </div>
      <Card
        headingLevel={2}
        title={team ? team.name : 'Nova equipa'}
        actions={
          team?.status === 'archived' ? <StatusBadge tone="neutral">Arquivada</StatusBadge> : null
        }
      >
        <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
          <div className="grid items-start gap-4 md:grid-cols-[14rem_1fr]">
            <ImageField
              label="Logótipo"
              optional
              aspect={1}
              value={logoUrl}
              onChange={setLogoUrl}
              onBusyChange={images.track('logotipo')}
              error={fields.logoUrl}
              hint="Quadrado, de preferência com fundo transparente."
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Nome" error={fields.name} className="sm:col-span-2">
                <Input
                  value={name}
                  maxLength={80}
                  disabled={locked}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
              <Field label="Jogo" error={fields.gameId}>
                <Select
                  value={gameId}
                  disabled={locked}
                  onChange={(e) => setGameId(e.target.value)}
                >
                  <option value="">Escolha…</option>
                  {catalog.divisions.map((d) => (
                    <optgroup key={d.id} label={d.name}>
                      {catalog.games
                        .filter((g) => g.divisionId === d.id)
                        .map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                </Select>
              </Field>
              <Field
                label="Nome de marca"
                optional
                error={fields.brandName}
                hint={
                  division?.brandName
                    ? `Vazio usa o da modalidade: ${division.brandName}.`
                    : 'Por exemplo Grow uP Casa Pia AC.'
                }
              >
                <Input
                  value={brandName}
                  maxLength={80}
                  disabled={locked}
                  onChange={(e) => setBrandName(e.target.value)}
                />
              </Field>
              <Field label="Tipo" hint={TEAM_KIND_HINTS[kind]}>
                <Select
                  value={kind}
                  disabled={locked}
                  onChange={(e) => setKind(teamKindSchema.parse(e.target.value))}
                >
                  {TEAM_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {TEAM_KIND_LABELS[k]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Lineup"
                hint={
                  lineupMode === 'season'
                    ? 'Guarda o lineup de cada época, com datas definidas pela equipa.'
                    : 'Só datas de entrada e saída de cada pessoa.'
                }
              >
                <Select
                  value={lineupMode}
                  disabled={locked}
                  onChange={(e) => setLineupMode(lineupModeSchema.parse(e.target.value))}
                >
                  {LINEUP_MODES.map((m) => (
                    <option key={m} value={m}>
                      {LINEUP_MODE_LABELS[m]}
                    </option>
                  ))}
                </Select>
              </Field>
              <div className="sm:col-span-2">
                <Checkbox
                  label="Academia (equipa de formação)"
                  checked={academy}
                  disabled={locked}
                  onChange={(e) => setAcademy(e.target.checked)}
                />
              </div>
            </div>
          </div>
          <Field
            label="Descrição curta"
            optional
            hint="Aparece na página da equipa no site."
            error={fields.description}
          >
            <Textarea
              rows={3}
              value={description}
              maxLength={600}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <ImageField
            label="Foto de grupo"
            optional
            aspect={16 / 9}
            value={photoUrl}
            onChange={setPhotoUrl}
            onBusyChange={images.track('foto')}
            error={fields.photoUrl}
            hint="Horizontal (16:9), para o topo da página da equipa."
          />
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {saved ? <Notice tone="success">{saved}</Notice> : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={busy || images.busy}>
              {team ? 'Guardar' : 'Criar equipa'}
            </Button>
            {team && canManageAll && team.status === 'active' ? (
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => void setStatus('archive')}
              >
                Arquivar
              </Button>
            ) : null}
            {team && canManageAll && team.status === 'archived' ? (
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => void setStatus('restore')}
              >
                Reativar
              </Button>
            ) : null}
          </div>
          {team ? (
            <p className="text-caption text-gu-ink-muted">
              Página pública: growup.gg/equipas/{team.slug} · Última alteração:{' '}
              {formatDateTime(team.updatedAt)} por {team.updatedBy}.
            </p>
          ) : null}
        </form>
      </Card>
      {team && canManageAll ? <ManagersCard team={team} onSaved={onSaved} /> : null}
      {team && canManageAll ? (
        <PartnersCard team={team} partners={partners} onSaved={onSaved} />
      ) : null}
      {team ? (
        <LineupCard team={team} game={catalog.games.find((g) => g.id === team.gameId)} />
      ) : null}
      {team ? <CalendarCard team={team} /> : null}
      {team ? <TripsCard team={team} /> : null}
      {team ? <TeamRulesCard team={team} /> : null}
      {team ? (
        <SheetsCard team={team} game={catalog.games.find((g) => g.id === team.gameId)} />
      ) : null}
      {team ? <ShirtsCard team={team} canChoose={canManageAll} /> : null}
    </div>
  );
}

const searchAccounts = (q: string) =>
  getJson(`${BASE}/accounts?q=${encodeURIComponent(q)}`, z.array(teamAccountSchema));

function ManagersCard({ team, onSaved }: { team: AdminTeam; onSaved: (t: AdminTeam) => void }) {
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (method: 'POST' | 'DELETE', path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      onSaved(
        adminTeamSchema.parse(await send(method, `${BASE}/${team.id}/managers${path}`, body)),
      );
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={2} title="Manager da equipa">
      <div className="flex flex-col gap-3">
        <p className="text-gu-ink-muted">
          O Manager vê esta equipa no back-office. Para entrar, precisa também do papel «Manager de
          equipa» em Staff.
        </p>
        {team.managers.length === 0 ? <p>Ainda sem manager.</p> : null}
        <ul className="flex flex-col gap-2">
          {team.managers.map((m) => (
            <li key={m.accountId} className="flex items-center gap-3">
              <span className="font-semibold">{m.login}</span>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                aria-label={`Tirar ${m.login}`}
                onClick={() => void run('DELETE', `/${m.accountId}`)}
              >
                Tirar
              </Button>
            </li>
          ))}
        </ul>
        <Field label="Adicionar manager">
          <Autocomplete
            value={q}
            onChange={setQ}
            search={searchAccounts}
            getKey={(a) => a.id}
            renderItem={(a) => <span className="truncate font-semibold">{a.login}</span>}
            onSelect={(a) => {
              setQ('');
              void run('POST', '', { accountId: a.id });
            }}
            placeholder="Procurar pelo login…"
          />
        </Field>
        {error ? <Notice tone="danger">{error}</Notice> : null}
      </div>
    </Card>
  );
}

/** Parceiros na pagina publica da equipa, pela ordem em que se marcam (RF-EQ13). */
function PartnersCard({
  team,
  partners,
  onSaved,
}: {
  team: AdminTeam;
  partners: TeamList['partners'];
  onSaved: (t: AdminTeam) => void;
}) {
  const [chosen, setChosen] = useState<string[]>(team.partnerIds);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const changed = chosen.join() !== team.partnerIds.join();

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(
        adminTeamSchema.parse(
          await send('PUT', `${BASE}/${team.id}/partners`, { partnerIds: chosen }),
        ),
      );
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={2} title="Parceiros da equipa">
      <div className="flex flex-col gap-3">
        <p className="text-gu-ink-muted">
          Aparecem na página da equipa, pela ordem em que os marcar. A lista vem de Conteúdos &gt;
          Parceiros (só os publicados).
        </p>
        {partners.length === 0 ? <p>Ainda não há parceiros publicados.</p> : null}
        <ul className="grid gap-2 sm:grid-cols-2">
          {partners.map((p) => {
            const at = chosen.indexOf(p.id);
            return (
              <li key={p.id}>
                <Checkbox
                  label={at >= 0 ? `${String(at + 1)}. ${p.name}` : p.name}
                  checked={at >= 0}
                  onChange={(e) => {
                    const on = e.currentTarget.checked;
                    setChosen((c) => (on ? [...c, p.id] : c.filter((id) => id !== p.id)));
                  }}
                />
              </li>
            );
          })}
        </ul>
        <div>
          <Button size="sm" disabled={busy || !changed} onClick={() => void save()}>
            Guardar parceiros
          </Button>
        </div>
        {error ? <Notice tone="danger">{error}</Notice> : null}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Jogos e modalidades
// ---------------------------------------------------------------------------

function GamesView({
  catalog,
  onChange,
}: {
  catalog: TeamCatalog;
  onChange: (c: TeamCatalog) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (method: string, path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      onChange(teamCatalogSchema.parse(await send(method, `${BASE}${path}`, body)));
      return true;
    } catch (e) {
      setError(messageOf(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {catalog.divisions.map((d) => (
        <Card key={d.id} headingLevel={2} title={d.name}>
          <div className="flex flex-col gap-4">
            <DivisionBrand
              division={d}
              busy={busy}
              onSave={(brandName) => run('PUT', `/divisions/${d.id}`, { brandName })}
            />
            <ul className="flex flex-col gap-2">
              {catalog.games
                .filter((g) => g.divisionId === d.id)
                .map((g) => (
                  <li key={g.id}>
                    <GameRow
                      game={g}
                      busy={busy}
                      onSave={(data) => run('PUT', `/games/${g.id}`, { divisionId: d.id, ...data })}
                      onRemove={() => run('DELETE', `/games/${g.id}`)}
                      onAddRole={(roleName) =>
                        run('POST', `/games/${g.id}/roles`, { name: roleName })
                      }
                      onRemoveRole={(roleId) => run('DELETE', `/game-roles/${roleId}`)}
                    />
                  </li>
                ))}
            </ul>
            <NewGame
              busy={busy}
              onAdd={(gameName) => run('POST', '/games', { divisionId: d.id, name: gameName })}
            />
          </div>
        </Card>
      ))}
    </div>
  );
}

function DivisionBrand({
  division,
  busy,
  onSave,
}: {
  division: TeamDivision;
  busy: boolean;
  onSave: (brandName: string) => Promise<boolean>;
}) {
  const [brand, setBrand] = useState(division.brandName);
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        void onSave(brand);
      }}
    >
      <Field label="Nome de marca da modalidade" optional className="min-w-64 flex-1">
        <Input value={brand} maxLength={80} onChange={(e) => setBrand(e.target.value)} />
      </Field>
      <Button
        type="submit"
        size="sm"
        variant="secondary"
        disabled={busy || brand === division.brandName}
      >
        Guardar marca
      </Button>
    </form>
  );
}

function GameRow({
  game,
  busy,
  onSave,
  onRemove,
  onAddRole,
  onRemoveRole,
}: {
  game: TeamGame;
  busy: boolean;
  onSave: (data: { name: string; logoUrl: string | null }) => Promise<boolean>;
  onRemove: () => Promise<boolean>;
  onAddRole: (name: string) => Promise<boolean>;
  onRemoveRole: (roleId: string) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [gameName, setGameName] = useState(game.name);
  const [logoUrl, setLogoUrl] = useState<string | null>(game.logoUrl);
  const images = useImagesBusy();

  if (editing) {
    return (
      <form
        className="flex flex-col gap-3 border-2 border-gu-line p-3"
        onSubmit={(e) => {
          e.preventDefault();
          void onSave({ name: gameName, logoUrl }).then((ok) => ok && setEditing(false));
        }}
      >
        <div className="grid items-start gap-4 md:grid-cols-[10rem_1fr]">
          <ImageField
            label="Logótipo"
            optional
            aspect={1}
            value={logoUrl}
            onChange={setLogoUrl}
            onBusyChange={images.track('jogo')}
          />
          <Field label="Nome do jogo">
            <Input value={gameName} maxLength={60} onChange={(e) => setGameName(e.target.value)} />
          </Field>
        </div>
        <div className="flex gap-2">
          <Button type="submit" size="sm" disabled={busy || images.busy}>
            Guardar
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
            Cancelar
          </Button>
        </div>
        <GameRoles game={game} busy={busy} onAdd={onAddRole} onRemove={onRemoveRole} />
      </form>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-gu-line py-2">
      {game.logoUrl ? (
        <img src={mediaVariant(game.logoUrl, 'sm')} alt="" className="size-8 object-contain" />
      ) : (
        <span className="size-8" />
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="font-semibold">{game.name}</span>
        {game.roles.length > 0 ? (
          <span className="text-caption text-gu-ink-muted">
            {game.roles.map((r) => r.name).join(' · ')}
          </span>
        ) : null}
      </span>
      <span className="text-caption text-gu-ink-muted">
        {game.teamCount === 1 ? '1 equipa' : `${String(game.teamCount)} equipas`}
      </span>
      <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
        Editar
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={busy || game.teamCount > 0}
        title={game.teamCount > 0 ? 'Um jogo com equipas não se apaga' : undefined}
        aria-label={`Apagar ${game.name}`}
        onClick={() => {
          if (window.confirm(`Apagar o jogo «${game.name}»?`)) void onRemove();
        }}
      >
        Apagar
      </Button>
    </div>
  );
}

/** Funcoes de jogo (Duelista, Suporte, Guarda-redes…), usadas no lineup. */
function GameRoles({
  game,
  busy,
  onAdd,
  onRemove,
}: {
  game: TeamGame;
  busy: boolean;
  onAdd: (name: string) => Promise<boolean>;
  onRemove: (roleId: string) => Promise<boolean>;
}) {
  const [roleName, setRoleName] = useState('');
  const add = () => void onAdd(roleName).then((ok) => ok && setRoleName(''));
  return (
    <div className="flex flex-col gap-2 border-t border-gu-line pt-3">
      <span className="font-sub text-label font-bold uppercase">Funções de jogo</span>
      {game.roles.length === 0 ? (
        <p className="text-caption text-gu-ink-muted">Nenhuma. São opcionais no lineup.</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {game.roles.map((r) => (
            <li key={r.id} className="flex items-center gap-1 border-2 border-gu-line px-2 py-1">
              {r.name}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                aria-label={`Apagar a função ${r.name}`}
                onClick={() => void onRemove(r.id)}
              >
                ×
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Nova função de jogo" hint="Por exemplo Duelista." className="min-w-48 flex-1">
          <Input
            value={roleName}
            maxLength={40}
            onChange={(e) => setRoleName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
          />
        </Field>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={busy || roleName.trim() === ''}
          onClick={add}
        >
          Adicionar função
        </Button>
      </div>
    </div>
  );
}

function NewGame({ busy, onAdd }: { busy: boolean; onAdd: (name: string) => Promise<boolean> }) {
  const [gameName, setGameName] = useState('');
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        void onAdd(gameName).then((ok) => ok && setGameName(''));
      }}
    >
      <Field label="Novo jogo" className="min-w-64 flex-1">
        <Input value={gameName} maxLength={60} onChange={(e) => setGameName(e.target.value)} />
      </Field>
      <Button type="submit" size="sm" disabled={busy || gameName.trim() === ''}>
        Adicionar jogo
      </Button>
    </form>
  );
}
